// Kibu staff browsers. A staff member's email link arrives with ?kibu_internal=1 (the backend adds it), and visiting
// any page with ?kibu_internal=1 marks a device by hand; ?kibu_internal=0 clears it. A marked browser tells Google
// Analytics it's internal traffic and flags our own shop events and checkouts, so staff visits and test orders stay
// out of marketing results.

const INTERNAL_TRAFFIC_STORAGE_KEY = 'kibu_internal_traffic_v1'

const storage = (): Storage | undefined => {
    try {
        return typeof window === 'undefined' ? undefined : window.localStorage
    } catch {
        return undefined
    }
}

export type InternalTrafficChange = 'marked' | 'cleared'

// A change made by hand (no email click) on this page load, shown as a confirmation.
let manualChange: InternalTrafficChange | null = null

export const captureInternalTrafficFromLocation = (): void => {
    if (typeof window === 'undefined') {
        return
    }
    const params = new URLSearchParams(window.location.search)
    const flag = params.get('kibu_internal')
    try {
        if (flag === '1') {
            storage()?.setItem(INTERNAL_TRAFFIC_STORAGE_KEY, '1')
        } else if (flag === '0') {
            storage()?.removeItem(INTERNAL_TRAFFIC_STORAGE_KEY)
        } else {
            return
        }
    } catch {
        return
    }
    // Staff arriving from an email link are marked quietly; the ERP's "Mark this browser as staff" gets a confirmation.
    if (!params.get('kibu_click_id')) {
        manualChange = flag === '1' ? 'marked' : 'cleared'
    }
}

/** The hand-made change from this page load, if there was one. */
export const getManualInternalTrafficChange = (): InternalTrafficChange | null => manualChange

export const isInternalTraffic = (): boolean => {
    try {
        return storage()?.getItem(INTERNAL_TRAFFIC_STORAGE_KEY) === '1'
    } catch {
        return false
    }
}

/** Google Analytics' standard marker for internal traffic, picked up by its "Internal traffic" data filter. */
export const tagGoogleAnalyticsInternalTraffic = (): void => {
    if (typeof window !== 'undefined' && window.gtag && isInternalTraffic()) {
        // gtag('set', {...}) is valid gtag.js; the shop's Window type only declares the (command, target) form.
        const gtag = window.gtag as unknown as (command: 'set', parameters: Record<string, string>) => void
        gtag('set', { traffic_type: 'internal' })
    }
}
